import { get, join, pick } from 'lodash';
import analytics from './config';
import { hasAnalyticsConsent, getSessionId } from './utils/cookies';
import { v4 as uuidv4 } from 'uuid';
import { hashCode } from '@/utils/stringUtils';

// Base event structure
export interface BaseEventStructure {
    event: string;
    event_id: string;
    event_version: string;
    event_timestamp: string;
    user_id: string;
    session_id: string;
    learning_path_id?: string;
    user_session_id?: string;
    application_details: {
        app_name: string;
        platform: string;
        device_id: string;
        ip_address: string;
    };
    event_specific_payload: Record<string, any>;
    category: string;
}

// Navigation Click Event
export interface NavigationClickPayload {
    navigation_item_id: string;
    navigation_item_name: string;
    target_url: string;
}

export interface NavigationClickEvent extends BaseEventStructure {
    event: 'navigation_clicked';
    event_specific_payload: NavigationClickPayload;
}

// Session Started Event
export interface SessionStartPayload {
    entry_point: 'direct_open' | 'notification_click' | 'external_link';
}

export interface SessionStartedEvent extends BaseEventStructure {
    event: 'session_started';
    event_specific_payload: SessionStartPayload;
}

// Base event properties
interface BaseEventProperties {
    sessionId?: string;
    userId?: string;
    timestamp?: string;
    userAgent?: string;
    language?: string;
}

// Common Event Properties
export interface CommonEventProperties {
    event_id: string;
    event_version: string;
    application_details: {
        app_name: string;
        platform: string;
        device_id?: string;
        ip_address?: string;
    };
}

// Learning event types
export interface LearningEventProperties extends BaseEventProperties {
    lessonId?: string;
    exerciseId?: string;
    skillType?: 'vocabulary' | 'grammar' | 'speaking' | 'listening' | 'reading' | 'writing';
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    score?: number;
    timeSpent?: number;
    completed?: boolean;
}

// User event types
export interface UserEventProperties extends BaseEventProperties {
    action?: string;
    category?: string;
    label?: string;
    value?: number;
}

// New Event Types
export interface VideoProgressProperties extends CommonEventProperties {
    lesson_id: string;
    content_id: string;
    content_type: 'video' | 'interactive_presentation_slide' | 'mind_map_node';
    content_name: string;
    view_duration_seconds?: number;
    completion_percentage?: number;
}

export interface LessonStartedProperties {
    lesson_id: string;
    lesson_name: string;
    lesson_type: string;
    lesson_session_id?: string;
    stage_id?: string;
    module_id?: string;
    source: 'dashboard_recommendation' | 'learning_path' | 'ai_suggestion' | 'scheduled_lesson';
    learning_path_id?: string;
}

export interface LessonCompletedProperties {
    lesson_id: string;
    lesson_name: string;
    lesson_type: string;
    lesson_session_id?: string;
    stage_id?: string;
    module_id?: string;
    duration_seconds: number;
    score?: number;
    max_score?: number;
    learning_path_id?: string;
}

export interface LessonAbandonedProperties {
    lesson_id: string;
    user_id: string;
    lesson_type: string;
    lesson_session_id?: string;
    duration_before_abandon: number;
    exit_method: 'closed_tab' | 'navigated_away' | 'inactivity_timeout';
    source?: 'ai_suggestion' | 'user_selected_topic' | 'learning_path';
    learning_path_id?: string;
}

export interface QuestionAnsweredProperties {
    lesson_id: string;
    lesson_session_id?: string;
    question_id: string;
    question_type: string;
    skill: string;
    user_answer: any;
    correct_answer?: any;
    is_correct: boolean;
    time_taken_seconds: number;
    attempts_count: number;
    learning_path_id?: string;
    score: number
}

export interface AISuggestionAcceptedProperties {
    suggested_lesson_id: string;
    suggested_lesson_type: string;
    suggestion_source_context: 'dashboard_daily_pick' | 'end_of_lesson_next_step';
}

// Helper function to add base properties
const addBaseProperties = (properties: any = {}): any => {
    return {
        sessionId: getSessionId(),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        language: navigator.language,
        event_id: uuidv4(),
        event_version: '1.0.0',
        application_details: {
            app_name: 'EMGLearningApp',
            platform: 'web',
            device_id: localStorage.getItem('device_id'),
            ip_address: undefined // Will be set by server
        },
        ...properties,
    };
};

// Check consent before tracking
const trackWithConsent = (eventName: string, properties: any = {}, keys?: string[]) => {
    if (!hasAnalyticsConsent()) {
        console.log('Analytics tracking skipped - no consent');
        return;
    }

    try {
        if (keys && keys.length > 0) {
            const hashed = JSON.stringify(pick({ ...get(properties, "event_specific_payload"), ...properties }, keys))
            if (localStorage.getItem(eventName) === hashed) {
                return;
            }
            localStorage.setItem(eventName, hashed)
        } else {
            const hashed = JSON.stringify(properties)
            if (localStorage.getItem(eventName) === hashed) {
                return;
            }
            localStorage.setItem(eventName, hashed)
        }

        analytics.track(eventName, properties);
    } catch (error) {
        console.error('Failed to track event:', eventName, error);
    }
};

// Page tracking
export const trackPageView = (pageName: string, properties: any = {}) => {
    if (!hasAnalyticsConsent()) return;

    try {
        analytics.page(pageName, properties);
    } catch (error) {
        console.error('Failed to track page view:', pageName, error);
    }
};

// User identification
export const identifyUser = (userId: string, traits: any = {}) => {
    if (!hasAnalyticsConsent()) return;

    try {
        analytics.identify(userId, {
            ...traits,
            sessionId: getSessionId(),
            timestamp: new Date().toISOString()
        });
    } catch (error) {
        console.error('Failed to identify user:', userId, error);
    }
};

// Authentication events
export const trackLogin = (method: string = 'email') => {
    const eventData = createEventStructure('user_login', {
        method,
        category: 'authentication'
    });
    trackWithConsent('user_login', eventData);
};

export const trackLogout = () => {
    const eventData = createEventStructure('user_logout', {
        category: 'authentication'
    });
    trackWithConsent('user_logout', eventData);
};

export const trackSignup = (method: string = 'email') => {
    const eventData = createEventStructure('user_signup', {
        method,
        category: 'authentication'
    });
    trackWithConsent('user_signup', eventData);
};

// Learning events
export const trackLessonStart = (properties: LearningEventProperties) => {
    const eventData = createEventStructure('learning_lesson_start', {
        ...properties,
        category: 'learning'
    });
    trackWithConsent('learning_lesson_start', eventData);
};

export const trackLessonComplete = (properties: LearningEventProperties) => {
    const eventData = createEventStructure('learning_lesson_complete', {
        ...properties,
        category: 'learning'
    });
    trackWithConsent('learning_lesson_complete', eventData);
};

export const trackExerciseStart = (properties: LearningEventProperties) => {
    const eventData = createEventStructure('learning_exercise_start', {
        ...properties,
        category: 'learning'
    });
    trackWithConsent('learning_exercise_start', eventData);
};

export const trackExerciseComplete = (properties: LearningEventProperties) => {
    const eventData = createEventStructure('learning_exercise_complete', {
        ...properties,
        category: 'learning'
    });
    trackWithConsent('learning_exercise_complete', eventData);
};

// Create event structure
const createEventStructure = (eventName: string, specificPayload: any): BaseEventStructure => {
    return {
        event: eventName,
        event_id: uuidv4(),
        event_version: '1.0.0',
        event_timestamp: new Date().toISOString(),
        user_id: localStorage.getItem('user_id') || 'anonymous',
        session_id: getSessionId() || 'unknown',
        application_details: {
            app_name: 'EMGLearningApp',
            platform: 'web',
            device_id: localStorage.getItem('device_id') || 'unknown',
            ip_address: '' // Will be filled by backend
        },
        event_specific_payload: specificPayload,
        category: specificPayload?.category ? specificPayload?.category : 'other',
    };
};

// Track navigation clicks
export const trackNavigationClicked = (payload: NavigationClickPayload) => {
    const eventData = createEventStructure('navigation_clicked', payload);
    trackWithConsent('navigation_clicked', eventData);
};

// Track session start
export const trackSessionStarted = (payload: SessionStartPayload) => {
    const eventData = createEventStructure('session_started', payload);
    trackWithConsent('session_started', eventData);
};

export const trackVideoProgress = (properties: VideoProgressProperties) => {
    const eventData = createEventStructure('video_progress_tracked', properties);
    trackWithConsent('video_progress_tracked', eventData);
};

export const trackDetailedLessonStarted = (properties: LessonStartedProperties) => {
    if (!properties.lesson_id || properties.lesson_id == undefined || !properties.learning_path_id) {
        return
    }
    const eventData = createEventStructure('lesson_started', properties);
    eventData.learning_path_id = get(properties, "learning_path_id")
    eventData.user_session_id = join([eventData.user_id, eventData.learning_path_id], "_")
    eventData.category = "learning"

    trackWithConsent('lesson_started', eventData, ["learning_path_id", "user_session_id", "lesson_session_id", "lesson_id"]);
};

export const trackDetailedLessonCompleted = (properties: LessonCompletedProperties) => {
    const eventData = createEventStructure('lesson_completed', properties);
    eventData.learning_path_id = get(properties, "learning_path_id")
    eventData.user_session_id = join([eventData.user_id, eventData.learning_path_id], "_")
    eventData.category = "learning"

    trackWithConsent('lesson_completed', eventData, ["learning_path_id", "user_session_id", "lesson_session_id", "lesson_id"]);
};

export const trackLessonAbandoned = (properties: LessonAbandonedProperties) => {
    const eventData = createEventStructure('lesson_abandoned', properties);
    eventData.learning_path_id = get(properties, "learning_path_id")
    eventData.user_session_id = join([eventData.user_id, eventData.learning_path_id], "_")
    eventData.category = "learning"

    trackWithConsent('lesson_abandoned', eventData, ["learning_path_id", "user_session_id", "lesson_session_id", "lesson_id"]);
};

export const trackQuestionAnswered = (properties: QuestionAnsweredProperties) => {
    const eventData = createEventStructure('question_answered', properties);
    eventData.learning_path_id = get(properties, "learning_path_id")
    eventData.user_session_id = join([eventData.user_id, eventData.learning_path_id], "_")
    eventData.category = "learning"

    trackWithConsent('question_answered', eventData, ["learning_path_id", "user_session_id", "lesson_session_id", "lesson_id", "question_id"]);
};

export const trackAISuggestionAccepted = (properties: AISuggestionAcceptedProperties) => {
    const eventData = createEventStructure('ai_suggestion_accepted', properties);
    trackWithConsent('ai_suggestion_accepted', eventData);
};

export const trackVocabularyPractice = (properties: LearningEventProperties & { wordId?: string; wordText?: string }) => {
    trackWithConsent('learning_vocabulary_practice', {
        ...properties,
        category: 'learning',
        skillType: 'vocabulary'
    });
};

export const trackSpeakingPractice = (properties: LearningEventProperties & { accuracy?: number; fluency?: number }) => {
    trackWithConsent('learning_speaking_practice', {
        ...properties,
        category: 'learning',
        skillType: 'speaking'
    });
};

export const trackAssessmentComplete = (properties: LearningEventProperties & { level?: string; recommendations?: string[] }) => {
    trackWithConsent('learning_assessment_complete', {
        ...properties,
        category: 'assessment'
    });
};

// Engagement events
export const trackStreakAchieved = (streakDays: number) => {
    const eventData = createEventStructure('engagement_streak_achieved', {
        streakDays,
        category: 'engagement'
    });
    trackWithConsent('engagement_streak_achieved', eventData);
};

export const trackAchievementUnlocked = (achievementId: string, achievementName: string) => {
    const eventData = createEventStructure('engagement_achievement_unlocked', {
        achievementId,
        achievementName,
        category: 'engagement'
    });
    trackWithConsent('engagement_achievement_unlocked', eventData);
};

export const trackFlowStateEntered = (duration: number) => {
    trackWithConsent('engagement_flow_state_entered', {
        duration,
        category: 'engagement'
    });
};

export const trackBreatheSessionComplete = (duration: number, cycles: number) => {
    trackWithConsent('engagement_breathe_session_complete', {
        duration,
        cycles,
        category: 'wellness'
    });
};

// UI interaction events
export const trackButtonClick = (buttonName: string, location: string) => {
    const eventData = createEventStructure('ui_button_click', {
        buttonName,
        location,
        category: 'ui_interaction'
    });
    trackWithConsent('ui_button_click', eventData);
};

export const trackLanguageSwitch = (fromLanguage: string, toLanguage: string) => {
    trackWithConsent('ui_language_switch', {
        fromLanguage,
        toLanguage,
        category: 'ui_interaction'
    });
};

export const trackSearchQuery = (query: string, resultsCount: number, section: string) => {
    const eventData = createEventStructure('ui_search', {
        query: query.toLowerCase(),
        resultsCount,
        section,
        category: 'ui_interaction'
    });
    trackWithConsent('ui_search', eventData);
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string, location: string) => {
    const eventData = createEventStructure('app_error', {
        errorType,
        errorMessage,
        location,
        category: 'error'
    });
    trackWithConsent('app_error', eventData);
};

// Performance tracking
export const trackPerformance = (metric: string, value: number, unit: string) => {
    const eventData = createEventStructure('app_performance', {
        metric,
        value,
        unit,
        category: 'performance'
    });
    trackWithConsent('app_performance', eventData);
};

// Cookie consent events
export const trackCookieConsentGiven = (consentTypes: string[]) => {
    // This should track even without consent as it's about giving consent
    try {
        analytics.track('cookie_consent_given', addBaseProperties({
            consentTypes,
            category: 'privacy'
        }));
    } catch (error) {
        console.error('Failed to track cookie consent given:', error);
    }
};

export const trackCookieConsentDeclined = () => {
    // This should track even without consent as it's about declining consent
    try {
        analytics.track('cookie_consent_declined', addBaseProperties({
            category: 'privacy'
        }));
    } catch (error) {
        console.error('Failed to track cookie consent declined:', error);
    }
};

// Export analytics instance for direct use if needed
export { analytics };


